/**
 * LastTouchCell - the lead's last real touch (docs/plans/crm-recruitment-list-plan.md Part A
 * section 4, B4): the channel, how long ago, and a snippet, as the inbox row shows it
 * (pages/Contacts.tsx ContactRow). System events are never the last touch (the server's embed
 * leaves them out). A reply still waiting on us reads in the brand color.
 */
import { dateTime, needsReply, relTime } from '../../../lib';
import { ChannelIcon } from '../../../components/shared';
import type { RecruitRow } from '../../types';

export interface LastTouchCellProps {
  row: RecruitRow;
  className?: string;
}

export function LastTouchCell({ row, className = '' }: LastTouchCellProps) {
  const lc = row.contact.last_comm;
  if (!lc) return <span className={`text-sm text-gray-400 dark:text-gray-500 ${className}`}>—</span>;
  const waiting = needsReply(lc);
  const from = lc.direction === 'inbound' ? 'from them' : lc.direction === 'outbound' ? 'from us' : '';
  return (
    <div className={`min-w-0 ${className}`} title={dateTime(lc.occurred_at)}>
      <div
        className={`flex items-center gap-1.5 text-xs ${
          waiting ? 'font-medium text-primary-700 dark:text-primary-400' : 'text-gray-600 dark:text-gray-300'
        }`}
      >
        <ChannelIcon channel={lc.channel} />
        <span className="whitespace-nowrap">{relTime(lc.occurred_at)}</span>
        {from && <span className="truncate text-[11px] font-normal text-gray-400 dark:text-gray-500">{from}</span>}
      </div>
      {lc.body && (
        <div className="mt-0.5 truncate text-xs text-gray-400 dark:text-gray-500">{lc.body}</div>
      )}
    </div>
  );
}

export default LastTouchCell;
